import React, { useState } from "react";
import CreatableSelect from "react-select/creatable";
import { components, ClearIndicatorProps, MultiValueGenericProps, MultiValueRemoveProps, OptionProps, SingleValueProps } from "react-select";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { ComposerRecipient, emailKey, parseRecipient, splitAddresses, uniqueRecipients } from "./composerAdapters";

const RecipientText = ({ value }: { value: ComposerRecipient }) => <span className="composer-recipient-text">
    {value.name && <strong>{value.name}</strong>}
    <span>{value.email}</span>
</span>;

const SingleValue = (props: SingleValueProps<ComposerRecipient, boolean>) => <components.SingleValue {...props}><RecipientText value={props.data} /></components.SingleValue>;
const MultiValueLabel = (props: MultiValueGenericProps<ComposerRecipient, boolean>) => <components.MultiValueLabel {...props}><RecipientText value={props.data} /></components.MultiValueLabel>;
const MultiValueRemove = (props: MultiValueRemoveProps<ComposerRecipient, boolean>) => {
    const { t } = useOfficeTranslation();
    return <components.MultiValueRemove {...props} innerProps={{ ...props.innerProps, "aria-label": t("email.composer.removeRecipient", { address: props.data.email }) }} />;
};
const ClearIndicator = (props: ClearIndicatorProps<ComposerRecipient, boolean>) => {
    const { t } = useOfficeTranslation();
    return <components.ClearIndicator {...props} innerProps={{
        ...props.innerProps, "aria-hidden": false, role: "button", tabIndex: 0,
        "aria-label": t("email.composer.clearRecipients"),
        onKeyDown: event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); props.clearValue(); } },
    }} />;
};
const Option = (props: OptionProps<ComposerRecipient, boolean>) => <components.Option {...props}>
    <RecipientText value={props.data} />
    {props.data.accountName && <small className="composer-recipient-account">{props.data.accountName}</small>}
</components.Option>;

interface Props {
    id: string;
    label: string;
    multiple?: boolean;
    value: ComposerRecipient[];
    input: string;
    contacts: ComposerRecipient[];
    disabled?: boolean;
    excludedEmail?: string;
    onChange: (values: ComposerRecipient[]) => void;
    onInputChange: (value: string) => void;
}

const RecipientField: React.FC<Props> = ({ id, label, multiple = false, value, input, contacts, disabled, excludedEmail, onChange, onInputChange }) => {
    const { t } = useOfficeTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const excluded = new Set([...(multiple ? value.map(item => emailKey(item.email)) : []), ...(excludedEmail ? [emailKey(excludedEmail)] : [])]);
    const addInput = () => {
        const tokens = splitAddresses(input);
        if (!tokens.length || (!multiple && tokens.length !== 1)) return false;
        const parsed = tokens.map(parseRecipient);
        if (parsed.some(item => !item)) return false;
        const recipients = parsed as ComposerRecipient[];
        const matched = recipients.map(item => {
            const matches = contacts.filter(contact => emailKey(contact.email) === emailKey(item.email));
            return matches.length === 1 ? matches[0] : item;
        });
        onChange(multiple ? uniqueRecipients([...value, ...matched]).filter(item => emailKey(item.email) !== emailKey(excludedEmail ?? "")) : matched);
        onInputChange("");
        setMenuOpen(false);
        return true;
    };

    // The select handles Enter first; cancel the browser's implicit form submit afterwards.
    return <div onKeyDown={event => { if (event.key === "Enter") event.preventDefault(); }}><CreatableSelect<ComposerRecipient, boolean>
        inputId={id}
        instanceId={id}
        aria-label={label}
        className="composer-recipient-select"
        classNamePrefix="composer-select"
        isMulti={multiple}
        isClearable
        isDisabled={disabled}
        menuIsOpen={menuOpen}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        value={multiple ? value : value[0] ?? null}
        inputValue={input}
        options={contacts.filter(item => !excluded.has(emailKey(item.email)))}
        getOptionValue={item => item.key}
        getOptionLabel={item => [item.name, item.email, item.accountName].filter(Boolean).join(" ")}
        components={{ SingleValue, MultiValueLabel, MultiValueRemove, ClearIndicator, Option }}
        placeholder={t("email.composer.recipientPlaceholder")}
        noOptionsMessage={() => t("email.composer.noContacts")}
        screenReaderStatus={({ count }) => t("email.composer.contactResults", { count })}
        ariaLiveMessages={{
            guidance: ({ context }) => t(context === "menu" ? "email.composer.menuGuidance" : "email.composer.inputGuidance"),
            onFilter: ({ resultsMessage }) => resultsMessage,
            onFocus: ({ label }) => label,
            onChange: ({ action, label, labels }) => action === "clear" ? t("email.composer.recipientsCleared")
                : t(action === "remove-value" || action === "pop-value" ? "email.composer.recipientRemoved" : "email.composer.recipientSelected", { address: label || labels.join(", ") }),
        }}
        formatCreateLabel={text => t("email.composer.useAddress", { address: text })}
        isValidNewOption={text => Boolean(parseRecipient(text)) && !excluded.has(emailKey(parseRecipient(text)?.email ?? ""))}
        getNewOptionData={text => parseRecipient(text)!}
        onChange={selected => onChange(selected ? Array.isArray(selected) ? [...selected] : [selected as ComposerRecipient] : [])}
        onCreateOption={() => { addInput(); }}
        onInputChange={(text, metadata) => { if (metadata.action === "input-change") onInputChange(text); }}
        onBlur={() => { if (input.trim()) addInput(); setMenuOpen(false); }}
        onKeyDown={event => {
            if (event.key === "Escape") { onInputChange(""); setMenuOpen(false); }
            if ((event.key === "," || event.key === ";" || event.key === "Enter") && addInput()) event.preventDefault();
        }}
    /></div>;
};

export default RecipientField;
